import { uploadMerchantOnboardingFile } from 'APIs/files/files';
import { MerchantFileUploadDocType } from 'APIs/files/files.types';
import {
  createMerchantPerson,
  deleteMerchantPerson,
  getMerchantPersons,
  updateMerchantPerson,
} from 'APIs/merchant.persons/merchant.persons';
import { MerchantPerson } from 'APIs/merchant.persons/merchant.persons.types';
import { downloadFile } from 'Components/Common/FileViewer';
import { LpBox } from 'Constants/styles';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';

import PersonList from './PersonList';
import { PersonFormProps, toCreateMerchantPersonRequest, toUpdateMerchantPersonRequest } from './types';

type Props = {
  merchantId: string;
};

const MerchantPersons = ({ merchantId }: Props) => {
  const { apiBaseUri, apiAprilBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
    apiAprilBaseUri: state.apiAprilBaseUri,
  }));

  const [actionPersons, setActionPersons] = useState<Array<string>>([]);
  const [persons, setPersons] = useState<Array<MerchantPerson>>([]);
  const [fetchingError, setFetchingError] = useState<string>('');
  const [merchantPerson, setMerchantPerson] = useState<MerchantPerson | null>(null);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');

  const getPerson = useCallback(
    (ownerId: string): MerchantPerson | null => {
      if (persons.length === 0) return null;
      const personToEdit = persons.filter((person) => person.ownerId === ownerId);

      if (personToEdit.length === 0) return null;
      return personToEdit[0];
    },
    [persons],
  );

  const fetchPersons = useCallback(async () => {
    if (!apiAprilBaseUri) {
      return;
    }

    try {
      setIsLoading(true);
      const resp = await getMerchantPersons(apiAprilBaseUri, merchantId);
      setPersons(resp);
    } catch (e) {
      setPersons([]);
      const errMsg = getApiErrorMessage(e);
      toast.error(`Error: ${errMsg}`);
      setFetchingError(errMsg);
    } finally {
      setIsLoading(false);
    }
  }, [apiAprilBaseUri, merchantId]);

  const deletePersonHandler = useCallback(
    async (ownerId: string) => {
      if (!apiAprilBaseUri) {
        return;
      }

      const mPerson = getPerson(ownerId);
      if (mPerson) {
        const confimed = window.confirm(
          `Are you sure you want to delete this beneficial owner (${mPerson.profile.givenName} ${mPerson.profile.familyName})?`,
        );
        if (!confimed) return;

        setActionPersons((prev) => [...prev, ownerId]);

        try {
          await deleteMerchantPerson(apiAprilBaseUri, merchantId, ownerId);
          toast.success('Beneficial owner deleted successfully');
          fetchPersons();
        } catch (error) {
          toast.error(`Error: ${getApiErrorMessage(error)}`);
        } finally {
          setActionPersons((prev) => prev.filter((pId) => pId !== ownerId));
        }
      }
    },
    [apiAprilBaseUri, fetchPersons, getPerson, merchantId],
  );

  const savePersonHandler = useCallback(
    async (formData: PersonFormProps) => {
      if (!apiAprilBaseUri) {
        return;
      }

      try {
        setIsModalLoading(true);
        setModalErrorMsg('');
        if (formData.ownerId) {
          const updatePerson = toUpdateMerchantPersonRequest(formData);
          await updateMerchantPerson(apiAprilBaseUri, merchantId, formData.ownerId, updatePerson);
          toast.success('Beneficial owner updated successfully');
        } else {
          const createPerson = toCreateMerchantPersonRequest(formData);
          await createMerchantPerson(apiAprilBaseUri, merchantId, createPerson);
          toast.success('Beneficial owner created successfully');
        }
        setShowModal(false);
        fetchPersons();
      } catch (error) {
        setModalErrorMsg(getApiErrorMessage(error));
      } finally {
        setIsModalLoading(false);
      }
    },
    [apiAprilBaseUri, fetchPersons, merchantId],
  );

  const handleFileUploads = useCallback(
    async (file: File, docType: MerchantFileUploadDocType): Promise<string | null> => {
      let fileId: string | null = null;
      try {
        const resp = await uploadMerchantOnboardingFile(apiBaseUri, merchantId, docType, file);
        fileId = resp.limeFileId;
      } catch (error) {
        console.log(getApiErrorMessage(error));
      } finally {
        return fileId;
      }
    },
    [apiBaseUri, merchantId],
  );

  const handleDownloadFile = useCallback(
    async (fileId: string): Promise<{ fileUrl: string; fileType: string } | null> => {
      return await downloadFile(apiBaseUri, merchantId, fileId);
    },
    [apiBaseUri, merchantId],
  );

  useEffect(() => {
    fetchPersons();
  }, [fetchPersons]);

  useEffect(() => {
    if (!showModal) {
      setMerchantPerson(null);
      setModalErrorMsg('');
    }
  }, [showModal]);

  return (
    <>
      <LpBox>
        <PersonList
          actionPersons={actionPersons}
          persons={persons}
          fetchingError={fetchingError}
          merchantPerson={merchantPerson}
          showModal={showModal}
          setShowModal={setShowModal}
          isModalLoading={isModalLoading}
          isLoading={isLoading}
          modalErrorMsg={modalErrorMsg}
          setMerchantPerson={setMerchantPerson}
          savePersonHandler={savePersonHandler}
          handleFileUploads={handleFileUploads}
          deletePersonHandler={deletePersonHandler}
          downloadFile={handleDownloadFile}
        />
      </LpBox>
    </>
  );
};

export default MerchantPersons;
